import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { initialTileState } from '../types/tileBuilderTypes';
import { RootState, useAppDispatch } from '../support/store';
import { getResolvedQueryResult } from '../components/Admin/TileBuilder/TilePreview/tilePreviewHelpers';
import { getPipelineStepOutputs } from '../support/customTileTransformUtils';
import { DataSource } from '../components/Admin/TileBuilder/TilePreview/tilePreviewTypes';
import { setSelectedSourceIdx } from '../support/slices/tileBuilderSlice';


export function useTilePreviewData(
  chartId: string,
) {
  const dispatch = useAppDispatch();
  const userData = useSelector((state: RootState) => state.user?.userData) as any;
  const { queries, transformations } = useSelector((state: RootState) => state.tileBuilder[chartId]?.tile || initialTileState);
  const { selectedSourceIdx, runtimeContext: previewRuntimeContext } = useSelector((state: RootState) => state.tileBuilder[chartId]?.preview || { selectedSourceIdx: 0, runtimeContext: {} });

    const fallbackVariables = useMemo(
      () => ({
        name: userData?.name ?? '',
        email: userData?.email ?? '',
        sfId: userData?.sfId ?? '',
      }),
      [userData]
    );

  const resolvedQueriesForPreview = useMemo(
    () => queries.map((q) => ({
      ...q,
      queryResult: getResolvedQueryResult(q, previewRuntimeContext, fallbackVariables) ?? [],
    })),
    [queries, previewRuntimeContext, fallbackVariables]
  );

  const allPipelineOutputs = useMemo(() => {
    try {
      return getPipelineStepOutputs(resolvedQueriesForPreview, transformations);
    } catch (e) {
      console.error('Pipeline outputs error', e);
      return [];
    }
  }, [resolvedQueriesForPreview, transformations]);

  const allSources = useMemo((): DataSource[] => [
    ...resolvedQueriesForPreview.map((q, i) => ({
      label: `Query ${i + 1} (${q.model || 'no model'})`,
      data: q.queryResult ?? [],
    })),
    ...transformations.map((t, i) => ({
      label: `Transformation ${i + 1} (${t.transformation.type})`,
      data: allPipelineOutputs[i] ?? [],
    })),
  ], [resolvedQueriesForPreview, transformations, allPipelineOutputs]);

  const previewData = useMemo(
    () => allSources[selectedSourceIdx]?.data ?? [],
    [allSources, selectedSourceIdx]
  );

  const derivedFields = useMemo(() => {
    if (!Array.isArray(previewData) || previewData.length === 0) return [];
    const firstItem = previewData[0];
    if (typeof firstItem !== 'object' || firstItem === null) return [];
    return Object.keys(firstItem).filter((key) => typeof key === 'string');
  }, [previewData]);

  useEffect(() => {
    dispatch(setSelectedSourceIdx({ chartId, index: allSources.length > 0 ? allSources.length - 1 : 0 })) ;
  }, [allSources.length, chartId, dispatch]);
    
  return {
    resolvedQueriesForPreview,
    allSources,
    previewData,
    derivedFields,
    selectedSourceIdx,
  };
}